import { flattenKerningMap } from './delete-glyphs-preflight';
export function isKerningRow(value) {
    return value instanceof Map || (!!value && typeof value === 'object');
}
export function getFlatKerningPairKey(firstKey, secondKey) {
    return `${firstKey}:${secondKey}`;
}
function getFlatKerningPairValue(kerning, firstKey, secondKey) {
    const flatKey = getFlatKerningPairKey(firstKey, secondKey);
    if (kerning instanceof Map) {
        const value = kerning.get(flatKey);
        return typeof value === 'number' ? value : null;
    }
    const value = kerning[flatKey];
    return typeof value === 'number' ? value : null;
}
export function usesFlatKerningPairs(kerning) {
    if (!kerning) {
        return false;
    }
    if (kerning instanceof Map) {
        for (const [key, value] of kerning.entries()) {
            if (typeof value === 'number' || key.includes(':')) {
                return true;
            }
        }
        return false;
    }
    return Object.entries(kerning).some(([key, value]) => typeof value === 'number' || key.includes(':'));
}
export function getKerningPairValue(kerning, firstKey, secondKey) {
    if (!kerning) {
        return null;
    }
    const flatValue = getFlatKerningPairValue(kerning, firstKey, secondKey);
    if (flatValue !== null) {
        return flatValue;
    }
    if (kerning instanceof Map) {
        const row = kerning.get(firstKey);
        if (!isKerningRow(row)) {
            return null;
        }
        if (row instanceof Map) {
            const value = row.get(secondKey);
            return typeof value === 'number' ? value : null;
        }
        const value = row[secondKey];
        return typeof value === 'number' ? value : null;
    }
    const row = kerning[firstKey];
    if (!isKerningRow(row)) {
        return null;
    }
    if (row instanceof Map) {
        const value = row.get(secondKey);
        return typeof value === 'number' ? value : null;
    }
    const value = row[secondKey];
    return typeof value === 'number' ? value : null;
}
export function getOrderedKerningPairKey(firstKey, secondKey) {
    return `${firstKey}\u0000${secondKey}`;
}
export function collectKerningGroupMemberships(groups, glyphName) {
    if (!groups || !glyphName) {
        return [];
    }
    const memberships = [];
    for (const [groupName, members] of Object.entries(groups)) {
        if (!Array.isArray(members) || !members.includes(glyphName)) {
            continue;
        }
        memberships.push(groupName);
    }
    memberships.sort((left, right) => left.localeCompare(right));
    return memberships;
}
export function getKerningOperandKeys(glyphName, groups) {
    return [
        glyphName,
        ...collectKerningGroupMemberships(groups, glyphName).map((name) => `@${name}`)
    ];
}
/**
 * Glyph-vs-class precedence for resolving which kerning rule applies:
 * glyph–glyph, glyph–group, group–glyph, then group–group.
 */
export function buildOrderedKerningPairs(firstKeys, secondKeys) {
    const glyphFirstKeys = firstKeys.filter((key) => !key.startsWith('@'));
    const groupFirstKeys = firstKeys.filter((key) => key.startsWith('@'));
    const glyphSecondKeys = secondKeys.filter((key) => !key.startsWith('@'));
    const groupSecondKeys = secondKeys.filter((key) => key.startsWith('@'));
    const orderedPairs = [];
    const seenPairKeys = new Set();
    const appendPairs = (currentFirstKeys, currentSecondKeys) => {
        for (const firstKey of currentFirstKeys) {
            for (const secondKey of currentSecondKeys) {
                const pairKey = getOrderedKerningPairKey(firstKey, secondKey);
                if (seenPairKeys.has(pairKey)) {
                    continue;
                }
                seenPairKeys.add(pairKey);
                orderedPairs.push({ firstKey, secondKey, pairKey });
            }
        }
    };
    appendPairs(glyphFirstKeys, glyphSecondKeys);
    appendPairs(glyphFirstKeys, groupSecondKeys);
    appendPairs(groupFirstKeys, glyphSecondKeys);
    appendPairs(groupFirstKeys, groupSecondKeys);
    return orderedPairs;
}
/**
 * Pick the winning kerning value for a glyph pair using the same preference
 * as text-mode overlays: first non-zero defined pair in precedence order,
 * else first defined pair (including explicit 0).
 */
export function resolvePreferredKerningPairValue(kerning, firstKeys, secondKeys) {
    if (!kerning) {
        return null;
    }
    const preferredPairs = buildOrderedKerningPairs(firstKeys, secondKeys);
    for (const { firstKey, secondKey } of preferredPairs) {
        const value = getKerningPairValue(kerning, firstKey, secondKey);
        if (value !== null && value !== 0) {
            return value;
        }
    }
    for (const { firstKey, secondKey } of preferredPairs) {
        const value = getKerningPairValue(kerning, firstKey, secondKey);
        if (value !== null) {
            return value;
        }
    }
    return null;
}
/**
 * Overlay kerning for two glyph names: glyph keys first, then `@group`
 * memberships, using the same preference as text-mode overlays.
 */
export function resolveKerningValueForGlyphPair(kerning, firstGlyphName, secondGlyphName, firstGroups, secondGroups) {
    return (resolvePreferredKerningPairValue(kerning, getKerningOperandKeys(firstGlyphName, firstGroups), getKerningOperandKeys(secondGlyphName, secondGroups)) ?? 0);
}
function rebuildAutomaticLigaturesAfterKerningPair(master, firstKey, secondKey, isRTL) {
    if (isRTL) {
        return;
    }
    const parent = master.parent?.();
    const host = parent;
    host?.rebuildAutomaticCompositesForKerningPair?.(firstKey, secondKey);
}
export function setKerningPairValueOnMaster(master, firstKey, secondKey, nextValue, isRTL = false) {
    try {
        const nextKerning = flattenKerningMap(isRTL ? master.kerning_rtl : master.kerning);
        const flatKey = getFlatKerningPairKey(firstKey, secondKey);
        if (nextValue === null) {
            delete nextKerning[flatKey];
        }
        else {
            nextKerning[flatKey] = nextValue;
        }
        if (isRTL) {
            master.kerning_rtl = nextKerning;
        }
        else {
            master.kerning = nextKerning;
        }
    }
    finally {
        rebuildAutomaticLigaturesAfterKerningPair(master, firstKey, secondKey, isRTL);
    }
}
export function getMasterDisplayLabel(master) {
    const name = master.name;
    if (typeof name === 'string') {
        return name;
    }
    if (name && typeof name === 'object') {
        return String(name.dflt ||
            name.en ||
            Object.values(name)[0] ||
            master.id);
    }
    return master.id;
}
